import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { ref, watch, onMounted, onBeforeUnmount, PropType } from 'vue'

/** Figma node 2724:7284 尺寸 */
const FIGMA_W = 520
const FIGMA_H = 59

const cols = 4
const rows = 3
const sectorAlpha = 0.92

/**
 * Figma 高保真路径（4列 × 3行）
 * 来源：https://www.figma.com/design/TROUsuz4Dr2YojHmRdkRSK/...?node-id=2724-7284
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    levels: {
        type: Array as PropType<number[]>,
        default: () => [0, 0, 0, 0]
    }
},
  setup(__props) {

const COLORS = {
    green: '#51E66D',
    yellow: '#FFC700',
    red: '#FF4444'
} as const

const props = __props

const canvasRef = ref<HTMLCanvasElement | null>(null)
let ctx: CanvasRenderingContext2D | null = null
let resizeObserver: ResizeObserver | null = null

const CELL_PATHS: string[][] = [
    [
        'M147.274 13.4872C98.5226 16.3281 52.0804 20.9751 8.84465 27.1666L0 18.3372C44.9857 11.6572 92.9384 6.65204 143.06 3.57218L147.274 13.4872Z',
        'M256.599 10.2523C221.473 10.3151 187.115 11.2958 153.807 13.119L149.584 3.18194C184.326 1.17063 220.085 0.0822607 256.599 0.00200122V10.2523Z',
        'M262.757 0C299.468 0.0332397 335.423 1.08382 370.357 3.06887L366.096 13.092C332.613 11.2679 298.071 10.2953 262.757 10.2503V0Z',
        'M376.884 3.45111C427.008 6.47723 474.979 11.4288 520 18.054L510.954 27.0835C467.742 20.9135 421.336 16.2858 372.629 13.4602L376.884 3.45111Z'
    ],
    [
        'M154.208 29.8012C108.663 32.5435 65.2167 37.0107 24.6686 42.9632L14.2924 32.6049C56.6885 26.672 102.148 22.2225 149.83 19.5009L154.208 29.8012Z',
        'M256.599 26.6473C223.861 26.7206 191.823 27.6694 160.736 29.421L156.366 19.1387C188.859 17.3991 222.358 16.4614 256.599 16.4V26.6473Z',
        'M262.757 16.398C297.187 16.4421 330.869 17.3732 363.537 19.1137L359.19 29.3389C327.917 27.6043 295.688 26.6801 262.757 26.6433V16.398Z',
        'M370.073 19.4739C417.71 22.1805 463.133 26.6111 505.504 32.5228L495.262 42.7481C454.697 36.8394 411.25 32.4155 365.72 29.7141L370.073 19.4739Z'
    ],
    [
        'M161.149 46.1311C118.851 48.8129 78.4713 53.1796 40.7331 59L30.1003 48.3855C69.8069 42.6959 112.277 38.4294 156.762 35.8088L161.149 46.1311Z',
        'M256.599 43.0413C226.241 43.1087 196.522 44.0297 167.67 45.7329L163.293 35.4366C193.564 33.7706 224.744 32.8666 256.599 32.795V43.0413Z',
        'M262.757 32.791C294.806 32.827 326.176 33.705 356.633 35.3546L352.234 45.7049C323.207 44.0006 293.305 43.0877 262.757 43.0393V32.791Z',
        'M363.167 35.7218C407.636 38.3023 450.103 42.5299 489.825 48.1764L479.072 58.9099C441.357 53.1121 401.011 48.7666 358.755 46.1011L363.167 35.7218Z'
    ]
]

function normalizeLevel(value: unknown): number {
    const num = Number(value)
    if (!Number.isFinite(num)) return 0
    return Math.min(3, Math.max(0, Math.round(num)))
}

function drawCell(row: number, col: number, color: string) {
    if (!ctx) return
    ctx.save()
    ctx.globalAlpha = sectorAlpha
    ctx.fillStyle = color
    ctx.fill(new Path2D(CELL_PATHS[row][col]))
    ctx.restore()
}

function render() {
    if (!ctx || !canvasRef.value) return

    const { width, height } = canvasRef.value
    ctx.clearRect(0, 0, width, height)

    const levels = (props.levels ?? []).slice(0, cols).map(normalizeLevel)
    while (levels.length < cols) levels.push(0)
    if (levels.every(v => v === 0)) return

    ctx.save()
    ctx.scale(width / FIGMA_W, height / FIGMA_H)

    for (let col = 0; col < cols; col++) {
        const level = levels[col]
        drawCell(0, col, level >= 1 ? COLORS.yellow : COLORS.green)
        if (level >= 2) drawCell(1, col, COLORS.red)
        if (level >= 3) drawCell(2, col, COLORS.red)
    }

    ctx.restore()
}

function initCanvas() {
    const el = canvasRef.value
    if (!el) return

    const width = el.clientWidth
    const height = el.clientHeight
    if (width <= 0 || height <= 0) return

    el.width = width
    el.height = height
    ctx = el.getContext('2d')
    render()
}

watch(
    () => (props.levels ?? []).slice(0, cols).join(','),
    () => render(),
    { immediate: true }
)

onMounted(() => {
    if (canvasRef.value) {
        resizeObserver = new ResizeObserver(initCanvas)
        resizeObserver.observe(canvasRef.value)
    }
    initCanvas()
})

onBeforeUnmount(() => {
    resizeObserver?.disconnect()
    resizeObserver = null
    ctx = null
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("canvas", {
    ref_key: "canvasRef",
    ref: canvasRef,
    class: "collision-canvas"
  }, null, 512))
}
}

})